import type { DataRoomDocument, ProfileEditFormData } from '@workspace/seedforge-features/profile'
import { ProfileEditForm } from '@workspace/seedforge-features/profile'
import { normalizeToMonth } from '@workspace/seedforge-shared'
import { getInitials } from '@workspace/ui/lib/utils'
import { useCallback, useMemo } from 'react'
import type { ExtractedStartupData, LinkedInProfile, WebsiteData } from '../lib/api.js'
import { OnboardingLayout } from './onboarding-layout.js'

interface ProfileReviewProps {
  linkedInProfile: LinkedInProfile | null
  extractedData: ExtractedStartupData | null
  websiteData: WebsiteData | null
  /** The website URL the user entered in the onboarding form */
  websiteUrl: string | null
  /** The signed-in user's name (e.g. from Google) — a fallback when there's no
   * LinkedIn scrape or deck extraction to name the founder from. */
  userName?: string
  error: string | null
  stageOptions?: { value: string; label: string }[]
  raiseStageOptions?: { value: string; label: string }[]
  categoryOptions?: { value: string; label: string }[]
  onConfirm: (data: {
    name: string
    description: string
    location: string
    websiteUrl: string
    categoryId: string
    tags: string[]
    productStage: string
    foundingDate: string
    isFundraising: boolean
    fundraisingAmount: string
    fundraisingStage: string
    documents: { documentId: string; type?: string }[]
    teamMembers: { name: string; position?: string; linkedinUrl?: string | null }[]
  }) => void
  onUploadFiles?: (files: File[]) => Promise<DataRoomDocument[]>
}

const HIDDEN_SECTIONS = new Set(['documents'])

export function ProfileReview({
  linkedInProfile,
  extractedData,
  websiteData,
  websiteUrl,
  userName,
  error,
  stageOptions,
  raiseStageOptions,
  categoryOptions,
  onConfirm,
  onUploadFiles,
}: ProfileReviewProps) {
  const founderName = linkedInProfile?.name ?? userName ?? extractedData?.founderNames?.[0] ?? ''

  const editFormData: ProfileEditFormData = useMemo(() => {
    const extractedStage = extractedData?.productStage ?? websiteData?.productStage ?? ''
    const stage = stageOptions?.some((opt) => opt.value === extractedStage) ? extractedStage : ''

    return {
      name: extractedData?.name ?? websiteData?.companyName ?? '',
      sector: '',
      sectorLabel: extractedData?.categoryName,
      stage,
      location: extractedData?.location ?? websiteData?.location ?? linkedInProfile?.location ?? '',
      founded: normalizeToMonth(extractedData?.foundingDate ?? websiteData?.foundingDate),
      website: websiteUrl ?? extractedData?.websiteUrl ?? linkedInProfile?.websiteUrl ?? '',
      description: extractedData?.description ?? websiteData?.description ?? '',
      tags: extractedData?.tags ?? websiteData?.tags ?? [],
      isFundraising: extractedData?.isFundraising ?? websiteData?.isFundraising ?? false,
      fundraisingRound: extractedData?.fundraisingStage ?? websiteData?.fundraisingStage ?? '',
      fundraisingTarget: extractedData?.fundraisingAmount ?? websiteData?.fundraisingAmount ?? '',
      team: founderName
        ? [
            {
              // The edit form keys row updates and validation errors by id.
              id: crypto.randomUUID(),
              initials: getInitials(founderName),
              name: founderName,
              role: 'Founder',
              linkedInUrl: linkedInProfile?.linkedinUrl,
            },
          ]
        : [],
      documents: [],
    }
  }, [extractedData, websiteData, websiteUrl, linkedInProfile, founderName, stageOptions])

  const handleFormSubmit = useCallback(
    (formData: ProfileEditFormData) => {
      onConfirm({
        name: formData.name,
        description: formData.description,
        location: formData.location,
        websiteUrl: formData.website,
        categoryId: formData.sector,
        tags: formData.tags,
        productStage: formData.stage,
        foundingDate: formData.founded,
        isFundraising: formData.isFundraising,
        fundraisingAmount: formData.fundraisingTarget,
        fundraisingStage: formData.fundraisingRound,
        documents: formData.documents
          .filter((d) => d.documentId)
          .map((d) => ({ documentId: d.documentId as string })),
        teamMembers: formData.team
          .filter((member) => member.name.trim())
          .map((member) => ({
            name: member.name.trim(),
            position: member.role || undefined,
            linkedinUrl: member.linkedInUrl ?? null,
          })),
      })
    },
    [onConfirm]
  )

  return (
    <OnboardingLayout error={error}>
      <ProfileEditForm
        data={editFormData}
        onSubmit={handleFormSubmit}
        onUploadFiles={onUploadFiles}
        submitLabel="Looks good"
        barMessage="Proceed to create your profile"
        discardLabel={null}
        hiddenSections={HIDDEN_SECTIONS}
        categoryOptions={categoryOptions}
        stageOptions={stageOptions}
        raiseStageOptions={raiseStageOptions}
      />
    </OnboardingLayout>
  )
}
